// RootCanalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const rootCanal: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Root Canal Treatment Dubai",
            "description": "Expert root canal treatment in Dubai to restore your smile comfortably. Pain-free procedure with experienced endodontists using modern techniques.",
            "robots": "index, follow",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Root Canal Treatment Dubai"],
        "hero": {
            "title": "Root Canal Treatment Dubai",
            "subtitle": "Restore Your Smile Comfortably with Expert Endodontist",
            "description": "Root canal treatment, or endodontic therapy, is a dental standard aiming to fix or save severely damaged, infected, or decaying teeth. By removing the infected pulp from inside the tooth, cleaning and disinfecting the root canal, and sealing it with a filling or crown, this procedure can relieve pain, preserve your natural teeth and restore full functionality to your smile."
        },
        "howItWorks": {
            "title": "10 Signs You May Need a Root Canal Treatment",
            "description": "Are you asking yourself if you need a root canal? Here are some of its more common symptoms: Ongoing toothache or sharp illness. A sensitivity to hot or cold temperatures. Swelling or tenderness in the gums around the affected tooth. The tooth is darkening or changed color. Little pimple-like bumps on the gums. Failure to heed these signs may result in serious infections, abscesses or tooth loss."
        },
        "procedureSteps": {
            "heading": "Complete Procedure for Root Canal Treatment in Dubai",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Our endodontist in Dubai will do a comprehensive physical examination during the first appointment and perform X-ray tests to evaluate damage and suggest the ideal treatment plan.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Anesthesia",
                    "desc": "The entire process is done under local anesthesia, thereby making it absolutely painless.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Cleaning & Shaping the Canal",
                    "desc": "Then the infected pulp is extracted, and modern instruments are used to clean and shape the root canal.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Filling & Sealing",
                    "desc": "A biocompatible material is then packed into the cleaned root canal to prevent reinfection.",
                    "icon": "fa-solid fa-fill-drip"
                },
                {
                    "title": "Restoration",
                    "desc": "To strengthen, restore function, and improve the appearance of the tooth, a dental crown is used to cover it.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Follow-Up",
                    "desc": "You will have follow-up care and recovery monitoring from our team.",
                    "icon": "fa-solid fa-heart-circle-check"
                }
            ]
        },
        "benefits": {
            "heading": "Reasons to Keep Your Natural Teeth with Root Canal Therapy",
            "subheading": "",
            "para": "By having a root canal, you can avoid extracting and replacing with an artificial tooth, as it allows you to save your natural teeth.",
            "items": [
                {
                    "title": "Preventative for jawbone loss",
                    "desc": "",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "Preserving chewing and speaking function",
                    "desc": "",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Misalignment of surrounding teeth is avoided",
                    "desc": "",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Results longer lasting with good care",
                    "desc": "",
                    "icon": "fa-solid fa-clock-rotate-left"
                }
            ]
        },
        "eligibility": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "preparationTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "results": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "aftercareTips": {
            "heading": "Post-Operative Care Guidelines for Root Canal",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Avoid chewing on treated tooth",
                    "desc": "Do not chew on the treated tooth until it is completely restored.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Pain management",
                    "desc": "Treat discomfort with over-the-counter pain relievers.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Maintain oral hygiene",
                    "desc": "Brush and floss regularly to keep teeth clean.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Follow-up appointments",
                    "desc": "Get follow-up appointments as they will help heal properly.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Avoid certain foods",
                    "desc": "Refrain from sticky or hard foods to prevent damaging the tooth.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is root canal treatment painful?",
                    "answer": "No. With modern anesthesia and technology, root canal treatment is virtually painless and much more comfortable than you might think."
                },
                {
                    "question": "How long does a root canal procedure take?",
                    "answer": "It usually takes 1-2 visits, depending on the complexity of the case."
                },
                {
                    "question": "How long does a root canal last?",
                    "answer": "With proper oral hygiene and regular check-ups, a root canal can last a lifetime."
                },
                {
                    "question": "How can I find an endodontist near me?",
                    "answer": "Our centrally located Root canal clinic near Jumeirah, Dubai makes it easy for patients to access high-quality root canal care."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    ar: {
        "url": "",
        "meta": {
            "title": "علاج قناة الجذر في دبي",
            "description": "علاج قناة الجذر في دبي مع أخصائيين ذوي خبرة. إجراء خالٍ من الألم باستخدام أحدث التقنيات للحفاظ على أسنانك الطبيعية.",
            "robots": "index, follow",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج قناة الجذر في دبي"],
        "hero": {
            "title": "علاج قناة الجذر في دبي",
            "subtitle": "استعد ابتسامتك بكل راحة مع أخصائي علاج جذور الأسنان",
            "description": "يعد علاج قناة الجذر أو علاج لب الأسنان إجراء طبي يُستخدم لإنقاذ الأسنان المتضررة بشدة أو المصابة أو المتحللة. يتم من خلال إزالة اللب المصاب من داخل السن، ثم تنظيف القنوات الجذرية وتعقيمها جيدًا، قبل أن يتم حشوها وإغلاقها بتاج أو حشوة مناسبة مما يخفف الألم، ويحافظ على الأسنان الطبيعية، ويعمل على استعادة الوظيفة الكاملة لابتسامتك."
        },
        "howItWorks": {
            "title": "10 علامات تشير إلى أنك قد تحتاج إلى علاج جذور الأسنان",
            "description": "هل تتساءل إذا كنت بحاجة إلى علاج عصب الأسنان؟ إليك بعض الأعراض الشائعة: ألم مستمر في السن أو نوبات ألم حادة. حساسية ملحوظة تجاه المشروبات أو الأطعمة الساخنة أو الباردة. تورم أو ألم في اللثة المحيطة بالسن المصاب. تغير في لون السن. ظهور نتوءات صغيرة تشبه الحبوب على اللثة."
        },
        "procedureSteps": {
            "heading": "الخطوات الكاملة لإجراء علاج قناة الجذر في دبي",
            "para": "",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "يقوم طبيب علاج العصب لدينا بإجراء فحص شامل خلال الموعد الأول، و إجراء أشعة سينية لتقييم مدى الضرر ووضع خطة العلاج المثالية لحالتك.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "التخدير",
                    "desc": "يُجرى العلاج بالكامل تحت تأثير التخدير الموضعي، مما يجعله خالياً تماماً من الألم.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "تنظيف وتشكيل القناة",
                    "desc": "يُزال اللب السني المصاب باستخدام أدوات متطورة، ثم تُنظف القنوات الجذرية وتُشكل بدقة.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "الحشو والإغلاق",
                    "desc": "يتم تعبئة القنوات الجذرية بعد التعقيم بمادة متوافقة حيوياً، لمنع تكرار العدوى وحماية السن.",
                    "icon": "fa-solid fa-fill-drip"
                },
                {
                    "title": "الترميم",
                    "desc": "يُركب تاج فوق السن المعالج، لتعزيز قوة السن واستعادة وظيفته الطبيعية وتحسين مظهره.",
                    "icon": "fa-solid fa-crown"
                },
                {
                    "title": "المتابعة",
                    "desc": "نحرص على تقديم رعاية ما بعد العلاج ومتابعة دورية لحالة السن، لضمان تعافي كامل ونتائج طويلة الأمد.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "benefits": {
            "heading": "أسباب للحفاظ على أسنانك الطبيعية من خلال علاج عصب الأسنان",
            "subheading": "",
            "para": "يُعد علاج جذور الأسنان خياراً مثالياً لتجنب خلع السن واستبداله بسن صناعي، حيث يتيح لك الحفاظ على أسنانك الطبيعية.",
            "items": [
                {
                    "title": "الوقاية من فقدان عظم الفك",
                    "desc": "",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "الحفاظ على وظيفة المضغ والنطق",
                    "desc": "",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "تجنب سوء اصطفاف الأسنان في المستقبل",
                    "desc": "",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "نتائج تدوم طويلاً مع العناية الجيدة",
                    "desc": "",
                    "icon": "fa-solid fa-clock-rotate-left"
                }
            ]
        },
        "eligibility": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "preparationTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "results": {
            "heading": "قبل وبعد العلاج",
            "subheading": "",
            "para": "",
            "items": []
        },
        "aftercareTips": {
            "heading": "إرشادات العناية بعد علاج قناة الجذر",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "تجنب المضغ على السن المُعالج",
                    "desc": "تجنب المضغ على السن المُعالج حتى يتم ترميمه بالكامل.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "تناول مسكنات الألم",
                    "desc": "تناول مسكنات الألم المتاحة دون وصفة طبية.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الحفاظ على نظافة الأسنان",
                    "desc": "نظف أسنانك بالفرشاة والخيط بانتظام للحفاظ على نظافة الأسنان.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "حضور مواعيد المتابعة",
                    "desc": "احرص على حضور مواعيد المتابعة مع طبيب الأسنان لتقييم تقدم العلاج.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "تجنب الأطعمة اللزجة أو الصلبة",
                    "desc": "تجنب الأطعمة اللزجة أو الصلبة لحماية الأسنان.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل علاج قناة الجذر مؤلم؟",
                    "answer": "لا، مع التخدير الحديث والتقنيات المتطورة، أصبح علاج قناة الجذر غير مؤلم تمامًا وأكثر راحة مما تتخيل."
                },
                {
                    "question": "كم تستغرق مدة إجراء علاج قناة الجذر؟",
                    "answer": "عادة ما يستغرق العلاج من 1-2 جلسات، حسب حالة السن ومدى تعقيدها."
                },
                {
                    "question": "كم تدوم نتائج علاج قناة الجذر؟",
                    "answer": "مع العناية الجيدة بالأسنان والزيارات الدورية لطبيب الأسنان، يمكن أن تدوم نتائج العلاج مدى الحياة."
                },
                {
                    "question": "كيف يمكنني العثور على أخصائي علاج عصب الأسنان بالقرب مني؟",
                    "answer": "تقع عيادتنا في موقع مركزي في دبي، مما يسهل الوصول إلينا إذا كنت تبحث عن أخصائي علاج قناة الجذر بالقرب منك."
                }
            ]
        },
        "treatmentAreas1": [
           
        ],
        "treatmentAreas2": [
           
        ]
    },
    shared: {
        images: {
            main: 'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/ebh-services-uae-58-scaled.jpg',
            beforeAfter: [
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/137.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/138.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/139.jpg'
            ],
            introductionImage: '',
        },
        product: {
            id: 23,
            title: 'Root Canal Treatment',
            price: 300,
            description: 'A root canal treatment is a dental procedure to treat infection at the center of a tooth.'
        }
    }
};